import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import HealingIcon from '@mui/icons-material/Healing';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import PersonIcon from '@mui/icons-material/Person';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import CustomWeekView from 'FRONTEND/components/Calendar/Calendar/CustomWeekView';
import {
  APPOINTMENT_KIND,
  AVAILABILITY_KIND,
  APPOINTMENT_STATUS_AWAITING_PAYMENT,
  APPOINTMENT_STATUS_AWAITING_CAPTURE,
  APPOINTMENT_STATUS_BOOKED,
  APPOINTMENT_STATUS_PROPOSED,
  APPOINTMENT_STATUS_CANCELLED,
  APPOINTMENT_STATUS_PENDING,
  MIN_APPOINTMENT_DISPLAY_DURATION,
  OTHER,
  VIDEO_CONSULTATION,
  VIDEO_SPECIALIST_ASSESSMENT,
  APPOINTMENT_STATUS_PENDING_OR_PROPOSED,
  BOOTH_TELECONSULTATION,
} from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { CALENDAR_DAY_END } from 'LEGACY/constants';


dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const computeColorShades = (r, g, b) => ({
  dark: `rgba(${Math.round(r * 0.6)}, ${Math.round(g * 0.6)}, ${Math.round(b * 0.6)}, 1)`,
  normal: `rgba(${r}, ${g}, ${b}, 1)`,
  dull: `rgba(${Math.round(r + ((255 - r) * 0.4))}, ${Math.round(g + ((255 - g) * 0.4))}, ${Math.round(b + ((255 - b) * 0.4))}, 1)`,
  dull2: `rgba(${Math.round(r + ((255 - r) * 0.8))}, ${Math.round(g + ((255 - g) * 0.8))}, ${Math.round(b + ((255 - b) * 0.8))}, 1)`,
  transparent: `rgba(${r}, ${g}, ${b}, .3)`,
  dullTransparent: `rgba(${Math.round(r + ((255 - r) * 0.8))}, ${Math.round(g + ((255 - g) * 0.8))}, ${Math.round(b + ((255 - b) * 0.8))}, .3)`,
});

export const COLORS = [
  computeColorShades(24, 118, 210), // rgba(24, 118, 210, 1)
  computeColorShades(41, 194, 30), // rgba(41, 194, 30, 1)
  computeColorShades(235, 196, 40), // rgba(235, 196, 40, 1)
  computeColorShades(217, 54, 0), // rgba(217, 54, 0, 1)
  computeColorShades(166, 29, 220), // rgba(166, 29, 220, 1)
];

export const DEFAULT_COLOR = computeColorShades(51, 122, 183);

export const getAppointmentsSlots = ({ acc, duration, availabilityStart, availabilityEnd, availabilityId }) => {
  if (!duration || duration <= 0) {
    console.warn('getAppointmentsSlots: duration is invalid or null', duration);
    return acc;
  }

  const endAppt = availabilityStart.clone().add(duration, 'minutes');
  const endOfDay = dayjs(CALENDAR_DAY_END).hour();

  // TODO: review calculation
  if (endAppt.isAfter(availabilityEnd) || endAppt.isAfter(endAppt.clone().hour(endOfDay))) {
    return acc;
  }

  acc.push({ start: availabilityStart, end: endAppt, availabilityId });
  getAppointmentsSlots({ acc, duration, availabilityStart: endAppt.clone(), availabilityEnd, availabilityId });

  return acc;
};

// Return true if the start and end dates do not conflict with any existing availability
export const isScheduleAvailable = ({ availabilities, startDate, endDate }) => {
  return availabilities.every((availability) => {
    return !dayjs(endDate).isSame(availability.startDate, 'day') ||
    dayjs(endDate).isSameOrBefore(dayjs(availability.startDate)) ||
    dayjs(startDate).isSameOrAfter(dayjs(availability.endDate));
  });
};

export const getCalendarMessages = () => {
  const calendarMessages = {
    today: i18n._('Today'),
    week: i18n._('Week'),
    day: i18n._('Day'),
    month: i18n._('Month'),
  };
  calendarMessages.next = '❯';
  calendarMessages.previous = '❮';
  return calendarMessages;
};

export const getCalendarViews = (isSmall) => isSmall ? { week: CustomWeekView, day: true } : { week: true, day: true };

export const setEventStyle = (contentEl, event, colorIndex, { forceDefaultColor = false } = {}) => {
  if (contentEl) {
    const eventEl = contentEl.closest('.rbc-event');
    const color = forceDefaultColor ? DEFAULT_COLOR : COLORS[colorIndex] ?? DEFAULT_COLOR;
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);

    eventEl.style.borderColor = color.normal;
    eventEl.style.padding = 0;

    // Each rbc-timeslot-group element has the height of 1 hour, and the width of a day column (if they're children of
    // an rbc-day-slot element)
    const sampleTimeslotGroup = document.getElementsByClassName('rbc-day-slot')[0].children[0];
    // Minimum height we can have is 15-minute events
    const eventUnitHeight = sampleTimeslotGroup.clientHeight / 4;

    const eventDurationMinutes = (endDate - startDate) / (1000 * 60);
    const newHeight = (eventDurationMinutes / MIN_APPOINTMENT_DISPLAY_DURATION) * eventUnitHeight;
    eventEl.style.height = `${newHeight}px`;
    eventEl.style.minHeight = `${eventUnitHeight}px`;

    if (eventDurationMinutes <= MIN_APPOINTMENT_DISPLAY_DURATION) {
      eventEl.style.fontSize = '11px';
      contentEl.style.fontSize = '11px';
    }

    switch (event.status) {
      case APPOINTMENT_STATUS_PENDING:
      case APPOINTMENT_STATUS_PROPOSED:
      case APPOINTMENT_STATUS_AWAITING_PAYMENT:
        eventEl.style.background = `linear-gradient(90deg, ${color.dull2} 4px, transparent 1%) center, linear-gradient(${color.dull2} 4px, transparent 1%) center, ${color.normal}`;
        eventEl.style.backgroundSize = '5px 5px';
        return;
      case APPOINTMENT_STATUS_BOOKED:
      case APPOINTMENT_STATUS_AWAITING_CAPTURE:
        eventEl.style.background = color.normal;
        eventEl.style.border = `1px solid ${color.dark}`;
        return;
      case APPOINTMENT_STATUS_CANCELLED:
        eventEl.style.background = `repeating-linear-gradient(
            135deg,
            ${color.dull},
            ${color.dull} 4px,
            ${color.dull2} 4px,
            ${color.dull2} 8px
          )
        `;
        return;
      // Availabilities
      default: {
        eventEl.style.background = color.transparent;
      }
    }
  }
};

export const generateRoomUrl = (baseUrl, roomName, { isMedicalConsultation = false, enableGuestAccess = false } = {}) => {
  return `${baseUrl}/conf?room=${encodeURIComponent(roomName)}&isEmbedded=true${isMedicalConsultation ? '&p2p=true&medicalConsultation=true' : ''}${enableGuestAccess ? '&guest' : ''}`;
};

export const getEventKindTranslationMap = () => {
  return {
    [AVAILABILITY_KIND]: i18n._('Availability'),
    [APPOINTMENT_KIND]: i18n._('Appointment'),
  };
};

export const getAppointmentTypeTranslationMap = () => {
  return {
    [VIDEO_CONSULTATION]: i18n._('Teleconsultation'),
    [VIDEO_SPECIALIST_ASSESSMENT]: i18n._('Tele-expertise / Tele-staff / Multidisciplinary team meeting'),
    [OTHER]: i18n._('Other (in-person, etc.)'),
    [BOOTH_TELECONSULTATION]: i18n._('Booth teleconsultation'),
  };
};

export const APPOINTMENT_TYPE_ICONS = {
  [VIDEO_CONSULTATION]: VideoCallIcon,
  [VIDEO_SPECIALIST_ASSESSMENT]: GroupsIcon,
  [OTHER]: MedicalServicesIcon,
  [BOOTH_TELECONSULTATION]: MedicalServicesIcon,
};

export const APPOINTMENT_DETAIL_ICONS = {
  calendar: CalendarTodayIcon,
  location: LocationOnIcon,
  person: PersonIcon,
  healing: HealingIcon,
  accept: CheckCircleOutlineIcon,
  decline: CancelOutlinedIcon,
};

export const getAppointmentStatusTranslationMap = () => {
  return {
    [APPOINTMENT_STATUS_AWAITING_PAYMENT]: i18n._('Awaiting payment'),
    [APPOINTMENT_STATUS_AWAITING_CAPTURE]: i18n._('Awaiting capture'),
    [APPOINTMENT_STATUS_BOOKED]: i18n._('Confirmed'),
    [APPOINTMENT_STATUS_PENDING_OR_PROPOSED]: i18n._('Proposed'),
    [APPOINTMENT_STATUS_CANCELLED]: i18n._('Cancelled'),
  };
};

export const filterDuplicateSlots = (slots) => {
  const seen = [];
  return slots.filter(slot => {
    const identifier = `${slot.start}-${slot.end}`;
    return seen.includes(identifier) ? false : seen.push(identifier);
  });
};

// Extract the rate from the code and format it for display.
export const getDisplayCode = (code, locale) => {
  return code.description + ' - ' + getDisplayAmount(code.activeFeeAmount, code.activeCurrency, locale);
};

// Extract the rate from the code and format it for display.
// TODO: Localize the currency symbol and format
export const getDisplayAmount = (amount, currency, locale) => {
  // If no cents, format as integer
  const minimumFractionDigits = amount % 100 === 0 ? 0 : 2;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
  }).format(parseFloat(amount / 100));
};


export const getCurrencySymbol = (locale, currency) => {
  if (!currency) {
    return '';
  }
  return (0).toLocaleString(locale, { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/\d/g, '').trim();
};
